import { DashboardRoutes } from "@/router";
import { useAppSelector, useAppTranslation, useLastUrlWasPage } from "@/utils";
import { Button, Drawer, Flex, Grid, useMessagedAsyncFn } from "@payping/web-components";
import { useState } from "react";
import { Close } from "@payping/icons";
import { useNavigate } from "react-router-dom";
import { CreatePlanEmployeeRequest, GuaranteeType } from "@payping/api/app/api/bnplMerchantTypes";
import { bnplMerchantApis } from "@payping/api";
import { apiErrorParser } from "@/utils/api-error-parser";
import BnplManagePlanPageSteps from "./Steps";
import PlanAmount from "./PlanAmount";
import PreInstallmentAmount from "./PreInstallmentAmount";
import InstallmentCount from "./InstallmentCount";
import ProfitPercent from "./ProfitPercent";
import PlanContract from "./PlanContract";
import SalaryDeduction from "./SalaryDeduction";
import { BnplPlanStepEnum } from "./types";

export interface PlanData {
  creditCode?: string;
  guaranteeType?: GuaranteeType;
  maxCreditAmount?: number;
  installmentCount?: number;
  contractIsMandatory?: boolean;
  employees?: CreatePlanEmployeeRequest[];
  employeesFile?: Blob;
  minPrePaymentRate?: number;
  interestRate?: number;
}
const BnplManagePlanPage = () => {
  const { t } = useAppTranslation();
  const [openDrawer, setOpenDrawer] = useState(true);
  const { navigateToLast } = useLastUrlWasPage();
  const { xl } = Grid.useBreakpoint();
  const [step, setStep] = useState<BnplPlanStepEnum>(BnplPlanStepEnum.PlanAmount);
  const [planData, setPlanData] = useState<PlanData>({});
  const contract = useAppSelector((state) => state.bnplMerchant.contract);
  const [selectedGuarantyItem, setSelectedGuarantyItem] = useState<GuaranteeType | undefined>(GuaranteeType.None);

  const navigate = useNavigate();
  const [{ loading }, createPlan] = useMessagedAsyncFn(
    async (data) => {
      try {
        const res = await bnplMerchantApis.createPlan(
          contract?.creditCode,
          data.guaranteeType,
          data.maxCreditAmount,
          data.installmentCount,
          data.contractIsMandatory,
          data.employees,
          data.employeesFile,
          data.minPrePaymentRate,
          data.interestRate,
        );
        navigate(DashboardRoutes.myShop.bnpl.plans.root.url);
        return res;
      } catch (error: any) {
        const parsedError = await apiErrorParser(error);
        if (parsedError.parsed) {
          throw parsedError.parsed;
        }
        throw parsedError.orginal;
      }
    },
    [],
    undefined,
  );

  return (
    <>
      <Drawer
        open={openDrawer}
        placement={"bottom"}
        height={"100%"}
        title={step < 5 ? t("shop.bnpl.InstallmentPlan") : t("shop.bnpl.InstallmentPlanWithSalary")}
        closeIcon={false}
        extra={
          <Button
            type={"text"}
            icon={<Close width={14} height={14} />}
            onClick={() => {
              setOpenDrawer(false);
              setPlanData({});
              navigateToLast({
                fallback() {
                  navigate(DashboardRoutes.myShop.bnpl.plans.root.url);
                },
              });
            }}
          />
        }
        classNames={{
          body: "!tw-p-0",
        }}
      >
        <Flex className="tw-min-h-full tw-w-full" vertical={!xl}>
          <BnplManagePlanPageSteps currentStep={step} showSalaryStep={selectedGuarantyItem === GuaranteeType.Salary} />
          <Flex vertical gap={"large"} className="tw-w-full tw-p-4 lg:tw-p-8">
            {step === BnplPlanStepEnum.PlanAmount ? (
              <PlanAmount
                onNext={(value) => {
                  setStep(BnplPlanStepEnum.PreInstallmentAmount);
                  planData.maxCreditAmount = value;
                  setPlanData({ ...planData });
                }}
                planData={planData}
              />
            ) : null}

            {step === BnplPlanStepEnum.PreInstallmentAmount ? (
              <PreInstallmentAmount
                onNext={(value) => {
                  setStep(BnplPlanStepEnum.InstallmentCount);
                  planData.minPrePaymentRate = value;
                  setPlanData({ ...planData });
                }}
                onPrev={() => setStep(BnplPlanStepEnum.PlanAmount)}
                planData={planData}
              />
            ) : null}

            {step === BnplPlanStepEnum.InstallmentCount ? (
              <InstallmentCount
                onNext={(value) => {
                  setStep(BnplPlanStepEnum.ProfitPercent);
                  planData.installmentCount = value;
                  setPlanData({ ...planData });
                }}
                onPrev={() => setStep(BnplPlanStepEnum.PreInstallmentAmount)}
                planData={planData}
              />
            ) : null}

            {step === BnplPlanStepEnum.ProfitPercent ? (
              <ProfitPercent
                onNext={(value) => {
                  setStep(BnplPlanStepEnum.PlanContract);
                  planData.interestRate = value;
                  setPlanData({ ...planData });
                }}
                onPrev={() => setStep(BnplPlanStepEnum.InstallmentCount)}
                planData={planData}
              />
            ) : null}

            {step === BnplPlanStepEnum.PlanContract ? (
              <PlanContract
                setSelectedGuarantyItem={setSelectedGuarantyItem}
                selectedGuarantyItem={selectedGuarantyItem}
                loading={loading}
                onNext={(value) => {
                  planData.guaranteeType = value.guaranteeType;
                  planData.contractIsMandatory = value.contractIsMandatory;
                  setPlanData({ ...planData });
                  if (planData.guaranteeType !== GuaranteeType.Salary) {
                    createPlan(planData);
                  } else {
                    setStep(BnplPlanStepEnum.SalaryDeduction);
                  }
                }}
                onPrev={() => setStep(BnplPlanStepEnum.ProfitPercent)}
                planData={planData}
              />
            ) : null}

            {step === BnplPlanStepEnum.SalaryDeduction ? (
              <SalaryDeduction
                onNext={(value) => {
                  planData.employees = value.employees;
                  planData.employeesFile = value.employeesFile;
                  setPlanData({ ...planData });
                  createPlan(planData);
                }}
                loading={loading}
                onPrev={() => setStep(BnplPlanStepEnum.PlanContract)}
              />
            ) : null}
          </Flex>
        </Flex>
        <div className="tw-pt-32" />
      </Drawer>
    </>
  );
};

export default BnplManagePlanPage;
